/**
 * Journey Board Component
 * Displays the cohort drill-down view with:
 * - Funnel cards (pipeline stages with customer counts)
 * - Executive read (auto-generated narrative)
 * - Deployment Mix, Top Engineering Friction, Top Patterns & Asks, Hottest Feedback Areas
 * - Customer chips and Live Preview Usage
 */

import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Skeleton,
    Chip,
    InputBase,
    ToggleButton,
    ToggleButtonGroup,
    alpha,
    Tooltip
} from "@mui/material";
import { Search16Regular } from "@fluentui/react-icons";
import {
    IJourneyBoardProps,
    IJourneyFunnelCard,
    ICustomerPerceptionCohortResponse,
    LivePreviewTimeRange
} from "./CustomerPerception.interfaces";
import { THEME, TIME_RANGE_OPTIONS, PIPELINE_COLORS, DEPLOYMENT_COLORS, FRICTION_COLORS } from "./CustomerPerception.constants";
import { JOURNEY_BOARD_STYLES, CUSTOMER_PERCEPTION_STYLES, FLOW_RIBBONS_STYLES } from "./CustomerPerception.styles";

/**
 * Horizontal Bar Chart for metrics visualization
 */
interface BarChartProps {
    items: Array<{ label: string; value: number; color?: string }>;
    maxValue?: number;
    unit?: string;
}

const HorizontalBarChart: React.FC<BarChartProps> = ({ items, maxValue: maxVal, unit = "cust" }) => {
    const maxValue = maxVal || Math.max(...items.map(i => i.value), 1);

    return (
        <Box>
            {items.slice(0, 7).map((item, idx) => {
                const pct = (item.value / maxValue) * 100;
                return (
                    <Box key={idx} sx={JOURNEY_BOARD_STYLES.barRow}>
                        <Tooltip title={item.label} arrow>
                            <Typography sx={JOURNEY_BOARD_STYLES.barLabel}>{item.label}</Typography>
                        </Tooltip>
                        <Box sx={JOURNEY_BOARD_STYLES.barContainer}>
                            <Box
                                sx={{
                                    ...JOURNEY_BOARD_STYLES.bar,
                                    width: `${pct}%`,
                                    backgroundColor: item.color || THEME.primary
                                }}
                            />
                        </Box>
                        <Typography sx={JOURNEY_BOARD_STYLES.barValue}>
                            {item.value} {unit}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Journey Board Section Component
 */
export const JourneyBoardSection: React.FC<IJourneyBoardProps> = ({
    funnelCards,
    cohortData,
    isLoading,
    isCohortLoading,
    selectedStage,
    onStageSelect
}) => {
    const [customerSearch, setCustomerSearch] = useState("");
    const [timeRange, setTimeRange] = useState<LivePreviewTimeRange>("Last 30 days");

    // Filtered customer chips
    const filteredChips = useMemo(() => {
        const chips = cohortData?.cohortCustomers || [];
        if (!customerSearch) return chips;
        const search = customerSearch.toLowerCase();
        return chips.filter(c =>
            c.customer.toLowerCase().includes(search) ||
            c.pm.toLowerCase().includes(search)
        );
    }, [cohortData?.cohortCustomers, customerSearch]);

    const handleStageClick = useCallback((stage: string) => {
        onStageSelect(stage);
    }, [onStageSelect]);

    const handleTimeRangeChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: LivePreviewTimeRange | null) => {
        if (newValue) {
            setTimeRange(newValue);
        }
    }, []);

    if (isLoading) {
        return (
            <Box>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} variant="rectangular" height={100} sx={{ flex: 1, borderRadius: "12px" }} />
                    ))}
                </Box>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: "12px" }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Title and Description */}
            <Paper elevation={0} sx={{ ...(CUSTOMER_PERCEPTION_STYLES.contentCard as object), mb: 2 }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1A1A2E", mb: 1 }}>
                    Journey Board — pick a stage, read the story
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 2 }}>
                    Click a pipeline stage to load its cohort: deployment mix, top blockers, hottest feedback areas, top asks, and an auto-written executive read.
                </Typography>

                {/* Selected Stage Badge */}
                {selectedStage && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                            Filtered to Preview Pipeline stage:
                        </Typography>
                        <Chip
                            label={selectedStage}
                            onDelete={() => onStageSelect("")}
                            size="small"
                            sx={{
                                backgroundColor: PIPELINE_COLORS[selectedStage] || "#0078D4",
                                color: "#FFFFFF",
                                fontWeight: 500
                            }}
                        />
                        <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                            — synced from the Flow tab
                        </Typography>
                    </Box>
                )}

                {/* How to Read */}
                <Box sx={CUSTOMER_PERCEPTION_STYLES.howToReadBox}>
                    <Typography component="span" sx={{ ...(CUSTOMER_PERCEPTION_STYLES.howToReadLabel as object) }}>
                        How to read it:
                    </Typography>
                    <Typography component="span" sx={CUSTOMER_PERCEPTION_STYLES.howToReadText}>
                        The funnel shows pipeline volume. Selecting a stage rewrites the panels below to just that cohort — turning raw counts into a <b>"so what"</b> for leadership.
                    </Typography>
                </Box>

                {/* Funnel Cards */}
                <Box sx={JOURNEY_BOARD_STYLES.funnelRow}>
                    {funnelCards.map(card => {
                        const isSelected = selectedStage === card.stage;
                        const bgColor = card.color || PIPELINE_COLORS[card.stage] || "#0078D4";

                        return (
                            <Paper
                                key={card.stage}
                                elevation={0}
                                sx={{
                                    ...JOURNEY_BOARD_STYLES.funnelCard as object,
                                    backgroundColor: bgColor,
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: `0 6px 16px ${alpha(bgColor, 0.4)}`
                                    },
                                    ...(isSelected ? JOURNEY_BOARD_STYLES.funnelCardSelected as object : {})
                                }}
                                onClick={() => handleStageClick(card.stage)}
                            >
                                <Typography sx={JOURNEY_BOARD_STYLES.funnelValue}>{card.customers}</Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.funnelLabel}>{card.stage}</Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.funnelPercent}>
                                    {card.pctOfPortfolio}% of portfolio
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>
            </Paper>

            {/* Cohort Data Panels */}
            {isCohortLoading ? (
                <Box sx={JOURNEY_BOARD_STYLES.panelsGrid}>
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} variant="rectangular" height={200} sx={{ borderRadius: "12px" }} />
                    ))}
                </Box>
            ) : cohortData ? (
                <>
                    {/* Executive Read */}
                    <Box sx={JOURNEY_BOARD_STYLES.executiveRead}>
                        <Typography sx={JOURNEY_BOARD_STYLES.executiveReadLabel}>Executive read:</Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.executiveReadText}>
                            Of <b>{cohortData.cohortSize}</b> customers in {selectedStage}, the most common deployment stage is{" "}
                            <span style={{ fontWeight: 600, color: "#0078D4", textDecoration: "underline" }}>{cohortData.executiveRead.topDeploymentStage}</span>
                            {" "}and <b>{cohortData.executiveRead.deployedOrPlanApproved}</b> are deployed or plan-approved.
                            The dominant engineering blocker is{" "}
                            <span style={{ fontWeight: 600, color: "#0078D4", textDecoration: "underline" }}>{cohortData.executiveRead.topFriction}</span>
                            {" "}({cohortData.executiveRead.topFrictionCustomerCount} customers)
                            {cohortData.executiveRead.topAsk && (
                                <>, and the loudest ask is <span style={{ fontWeight: 600, color: "#0078D4", textDecoration: "underline" }}>{cohortData.executiveRead.topAsk}</span></>
                            )}.
                        </Typography>
                    </Box>

                    {/* Metrics Panels Grid */}
                    <Box sx={JOURNEY_BOARD_STYLES.panelsGrid}>
                        {/* Deployment Mix */}
                        <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.panel}>
                            <Typography sx={JOURNEY_BOARD_STYLES.panelTitle}>DEPLOYMENT MIX</Typography>
                            <HorizontalBarChart
                                items={cohortData.deploymentMix.map(d => ({
                                    label: d.deploymentStage,
                                    value: d.customers,
                                    color: d.color || DEPLOYMENT_COLORS[d.deploymentStage] || THEME.primary
                                }))}
                            />
                        </Paper>

                        {/* Top Engineering Friction */}
                        <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.panel}>
                            <Typography sx={JOURNEY_BOARD_STYLES.panelTitle}>TOP ENGINEERING FRICTION</Typography>
                            <HorizontalBarChart
                                items={cohortData.topEngineeringFriction.map(f => ({
                                    label: f.friction,
                                    value: f.customers,
                                    color: f.color || FRICTION_COLORS[f.friction] || THEME.warning
                                }))}
                            />
                        </Paper>

                        {/* Hottest Feedback Areas */}
                        <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.panel}>
                            <Typography sx={JOURNEY_BOARD_STYLES.panelTitle}>HOTTEST FEEDBACK AREAS</Typography>
                            <HorizontalBarChart
                                items={cohortData.hottestFeedbackAreas.map(f => ({
                                    label: f.feedbackArea,
                                    value: f.submissions,
                                    color: THEME.primary
                                }))}
                                unit=""
                            />
                        </Paper>

                        {/* Top Patterns & Asks */}
                        <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.panel}>
                            <Typography sx={JOURNEY_BOARD_STYLES.panelTitle}>TOP PATTERNS & ASKS</Typography>
                            <HorizontalBarChart
                                items={cohortData.topPatternsAndAsks.map(p => ({
                                    label: p.ask,
                                    value: p.customers,
                                    color: THEME.success
                                }))}
                            />
                        </Paper>
                    </Box>

                    {/* Customers in Cohort */}
                    <Paper elevation={0} sx={FLOW_RIBBONS_STYLES.chipsSection}>
                        <Box sx={FLOW_RIBBONS_STYLES.chipsHeader}>
                            <Typography sx={FLOW_RIBBONS_STYLES.chipsTitle}>
                                CUSTOMERS · {selectedStage?.toUpperCase() || "ALL STAGES"} ({cohortData.cohortCustomers.length})
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "#F1F5F9", borderRadius: "20px", px: 1.5, py: 0.5 }}>
                                <Search16Regular style={{ color: "#94A3B8" }} />
                                <InputBase
                                    placeholder="Search customer..."
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    sx={{ fontSize: "13px", width: 150 }}
                                />
                            </Box>
                        </Box>
                        <Box sx={FLOW_RIBBONS_STYLES.chipsGrid}>
                            {filteredChips.length === 0 ? (
                                <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 3 }}>
                                    <Typography sx={{ color: "#64748B", fontSize: "13px" }}>
                                        No customers found in this cohort.
                                    </Typography>
                                </Box>
                            ) : (
                                filteredChips.map(chip => (
                                    <Tooltip
                                        key={chip.customer}
                                        title={`${chip.customer} · ${chip.deployStatus || "No status"} · ${chip.sessions} sessions`}
                                        arrow
                                    >
                                        <Box
                                            sx={{
                                                ...(FLOW_RIBBONS_STYLES.customerChip as object),
                                                borderLeftColor: FRICTION_COLORS[chip.topFriction || ""] || DEPLOYMENT_COLORS[chip.deployStatus || ""] || "#E2E8F0",
                                                borderLeftWidth: 3,
                                                "&:hover": {
                                                    background: "#E6F2FA",
                                                    borderColor: "#0078D4"
                                                }
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={FLOW_RIBBONS_STYLES.chipName}>{chip.customer}</Typography>
                                                <Typography sx={FLOW_RIBBONS_STYLES.chipPM}>PM · {chip.pm}</Typography>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                ))
                            )}
                        </Box>
                    </Paper>

                    {/* Live Preview Usage */}
                    <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.usageSection}>
                        <Box sx={JOURNEY_BOARD_STYLES.usageHeader}>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTitle}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTitleText}>LIVE PREVIEW USAGE</Typography>
                                <Box sx={JOURNEY_BOARD_STYLES.filteredBadge}>FILTERED TO SELECTION</Box>
                            </Box>
                            <ToggleButtonGroup
                                value={timeRange}
                                exclusive
                                onChange={handleTimeRangeChange}
                                size="small"
                            >
                                {TIME_RANGE_OPTIONS.map(opt => (
                                    <ToggleButton
                                        key={opt.key}
                                        value={opt.key}
                                        sx={{
                                            fontSize: "11px",
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: "none",
                                            "&.Mui-selected": {
                                                backgroundColor: THEME.primary,
                                                color: "#FFFFFF",
                                                "&:hover": { backgroundColor: THEME.primaryDark }
                                            }
                                        }}
                                    >
                                        {opt.text}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageSubtitle}>
                            Selection: {selectedStage} · {cohortData.livePreviewUsage?.customerCount || cohortData.cohortCustomers.length} of {cohortData.cohortCustomers.length} selected customers with telemetry · last 30 days · as of {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </Typography>
                        <Box sx={JOURNEY_BOARD_STYLES.usageTiles}>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                                    {cohortData.livePreviewUsage?.uniqueUsers || "—"}
                                </Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>UNIQUE USERS</Typography>
                            </Box>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                                    {cohortData.livePreviewUsage?.uniqueSessions || "—"}
                                </Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>UNIQUE SESSIONS</Typography>
                            </Box>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                                    {cohortData.livePreviewUsage?.totalSessions || "—"}
                                </Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>TOTAL SESSIONS</Typography>
                            </Box>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                                    {cohortData.livePreviewUsage?.pageViews?.toLocaleString() || "—"}
                                </Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>PAGE VIEWS</Typography>
                            </Box>
                            <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                                    {cohortData.livePreviewUsage?.playbookRuns || "—"}
                                </Typography>
                                <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>PLAYBOOK RUNS</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </>
            ) : (
                <Paper elevation={0} sx={{ ...CUSTOMER_PERCEPTION_STYLES.contentCard, textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: THEME.textSecondary }}>
                        Select a pipeline stage above to view cohort details
                    </Typography>
                </Paper>
            )}

            {/* Footer */}
            <Typography sx={JOURNEY_BOARD_STYLES.footer}>
                Perception Insights · design concepts · live sources: {funnelCards.reduce((sum, c) => sum + c.customers, 0)} customers, Customer Voice survey responses, CRM notes, ADO items
            </Typography>
        </Box>
    );
};

export default JourneyBoardSection;
